import { Paging } from '@Core';
import { IGetGeoZoneProps } from '@EcamModel/controllers/IGeoZoneHttpController';
import { ISendActionGet } from '@EcamModel/controllers/IPTZCamConfigureRequestHttpController';
import {
    CameraWithInfo,
    ContraventionReasonTranslations,
    GeoZoneCoordinates,
    GeoZones,
    GeoZoneType,
    Status,
    Location,
} from '@EcamModel/model';
import { CameraView, CameraViewType, PhotoSnapShotByPTZ } from '@EcamModel/model/CameraView';
import { GeoZoneOperationConfigGracePeriodWithDay } from '@EcamModel/model/GeoZoneOperationConfigGracePeriod';
import { Zones } from '@EcamModel/model/Zones';
import { IBreadCrumbs } from '@components/breadcumbs/BreadCumbs';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { pushError, pushSuccess } from '@components/toast';
import {
    cameraController,
    cameraViewController,
    geoZoneController,
    locationController,
    sp111ConfigureRequestHttpController,
} from '@controllers/index';
import { yupResolver } from '@hookform/resolvers/yup';
import useScrollToTop from '@hooks/useScrollToTop';
import { getGracePeriods } from '@pages/cameras/details/utils';
import { initPaging } from '@pages/cameras/list';
import { useImageDimensions } from '@pages/cameras/live-photos/components/PreviewImage';
import _, { groupBy } from 'lodash';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { useQueryClient } from 'react-query';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { cameraViewBlob } from '../..';
import {
    defaultGracePeriod,
    defaultOperationHours,
    detectModes,
    FormValues,
    handleConvertOperationHourConfig,
    hasInvalidGracePeriod,
    schema,
} from '../../../../AddGeoZones';
import { base64ToHTMLImageElement } from '../../components/PopupConfigViewCameraPTZ';
import { AddGzOverviewProps } from '../components/AddGeoZonesPTZ';

export type UpsertGeoZoneProps = {
    isCheckGeoZoneDetail?: boolean;
};

export default function useAddGeoZones({ isCheckGeoZoneDetail = false }: UpsertGeoZoneProps) {
    const { id: cameraId, geoZoneId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const location = useLocation();
    const state = location?.state;
    const geoZonesItem = state?.geoZonesItem as GeoZones;
    console.log('🚀 ~ useAddGeoZones ~ geoZonesItem:', geoZonesItem);
    const cameraViewZoomId = geoZonesItem?.CameraViewZoomId;

    const cameraView = state?.cameraView as CameraView;
    const listCameraView = state?.listCameraView as CameraView[];

    useScrollToTop();
    const [selectedCameraType, setSelectedCameraType] = useState<number>(
        cameraViewZoomId !== undefined || isCheckGeoZoneDetail ? 1 : 0
    );

    const isTypeOverview = selectedCameraType === 0;
    const isTypeDetails = selectedCameraType === 1;

    const isHasCameraViewZoomId = cameraViewZoomId;
    const typeDetailsIncludesCameraViewZoomId = Boolean(isTypeDetails || isHasCameraViewZoomId);

    const [camera, setCamera] = useState<CameraWithInfo>({} as CameraWithInfo);
    const [loading, setLoading] = useState<boolean>(true);
    const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
    const [isOverlap, setIsOverlap] = useState<boolean>(false);

    const [contraventionReasons, setContraventionReasons] = useState<ContraventionReasonTranslations[]>([]);
    const [startDate, setStartDate] = useState<Moment>(moment());
    const [blobBase64SP111, setBlobBase64SP111] = useState<string>('');
    const [imageElementSP111, setImageElementSP111] = useState<HTMLImageElement | null>(null);
    const [listGeoZonesByView, setListGeoZonesByView] = useState<Paging<GeoZones>>({ ...initPaging });
    const [loadingListGzByView, setLoadingListGzByView] = useState(true);
    const [zones, setZones] = useState<Zones[]>([]);

    const [filteredCamera, setFilteredCamera] = useState<ISendActionGet>({} as ISendActionGet);

    const { image: imageElementPTZ, loading: loadingImage } = useImageDimensions(
        `${cameraView?.LastPhotoBlob}?${JSON.stringify(cameraView?.Updated)}`,
        cameraViewBlob
    );

    const frameIndexItem = state?.frameIndex;
    const pathNameBreadCrumbs = `/cameras`;

    const breadcrumbs: IBreadCrumbs[] = [
        { id: 1, title: 'Camera list', href: `${pathNameBreadCrumbs}` },
        { id: 2, title: `${camera.Name}`, href: `${pathNameBreadCrumbs}/${cameraId}` },
        {
            id: 3,
            title: `Geo-zones overview ${frameIndexItem + 1}`,
            onBack: true,
        },
        { id: 4, title: `Add geo-zones`, isColorBlackText: true },
    ];

    const conditionBreadcrumbs =
        frameIndexItem !== undefined ? breadcrumbs : breadcrumbs.filter((item) => item.id !== 3);

    const methods = useForm<FormValues>({
        resolver: isTypeOverview ? undefined : yupResolver(schema),
        mode: 'all',
        defaultValues: {
            isExemptVRNs: false,
            contraventionReasonTranslations: undefined,
            detectMode: detectModes[0],
            isActive: true,
            geoZonePoints: [],
            operationHours: defaultOperationHours,
            gracePeriods: [defaultGracePeriod],
            currentgeoZonePoints: { points: [], totalBay: 0, geoZoneName: '' },
            multigeoZonePoints: [],
            geoZoneName: '',
            location: undefined,
            zone: undefined,
        },
    });

    const {
        watch,
        reset,
        control,
        setValue: _setValue,
        handleSubmit,
        setError,
        formState: { isDirty, isValid, errors, ...formState },
    } = methods;

    const setValue = _setValue as (name: string, value: any) => void;

    const formValues = watch();
    const geoZonePoints: GeoZoneCoordinates[] = formValues?.currentgeoZonePoints?.points?.length
        ? formValues.currentgeoZonePoints.points
        : formValues.geoZonePoints;

    const contraventionReasonTranslations = formValues.contraventionReasonTranslations;
    const detectMode = formValues.detectMode;

    const hasOperationHours = formValues.operationHours?.every((item) => !item.Status);
    const isHasInValidGracePeriod = hasInvalidGracePeriod(formValues.operationHours!, formValues.gracePeriods!);
    const isInvalidOperationHours = formValues.operationHours?.some(
        (item) => !!item.Status && item.TimeRanges.some((t) => !t.From || !t.To)
    );

    const isGeoZonePointsValid =
        geoZonePoints.length >= 3 &&
        geoZonePoints[0].XPoint === geoZonePoints[geoZonePoints.length - 1].XPoint &&
        geoZonePoints[0].YPoint === geoZonePoints[geoZonePoints.length - 1].YPoint;

    const cameraTypeConditions = {
        0: () => !isGeoZonePointsValid,
        1: () =>
            !(
                isDirty &&
                isValid &&
                contraventionReasonTranslations &&
                detectMode &&
                isGeoZonePointsValid &&
                operationHours.length > 0 &&
                !isOverlap &&
                !hasOperationHours &&
                isHasInValidGracePeriod &&
                !isInvalidOperationHours
            ),
    };

    const shouldDisableButton = () => cameraTypeConditions[selectedCameraType]?.() ?? true;

    const handleRadioChangeCameraType = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedCameraType(Number(event.target.value));
    };

    const handleUpsertGeoZoneOverview = async (
        _filter: GeoZones,
        cameraView?: CameraView,
        isNavigateAddGz: boolean = false
    ) => {
        setIsLoadingSave(true);
        await geoZoneController
            .upsertGeoZoneOverview(_filter)
            .then((res) => {
                if (isNavigateAddGz) {
                    navigate(`${pathNameBreadCrumbs}/${cameraId}/overview/${res?.Id}/add`, {
                        state: {
                            geoZonesItem: res,
                            cameraView: cameraView,
                        },
                        replace: true,
                    });

                    return;
                }

                navigate(`${pathNameBreadCrumbs}/${cameraId}`, {
                    state: { tabValue: !_.isNil(camera.PTZCameraId) ? 2 : 1, cameraViewId: cameraView?.Id },
                });
                pushSuccess('Geo-zone saved successfully');
                queryClient.invalidateQueries(['trailAuditEvent']);
                queryClient.invalidateQueries(['patrolTimeForecast']);
            })
            .catch((err) => {
                pushError(err.response.data.message);
            })
            .finally(() => {
                setIsLoadingSave(false);
            });
    };

    const handleNavigateButton = () => {
        navigate(`${pathNameBreadCrumbs}/${cameraId}`, {
            state: { tabValue: !_.isNil(camera.PTZCameraId) ? 2 : 1, cameraViewId: cameraView?.Id },
        });
    };

    const handleUpsertGeoZoneDetail = async (_filter: GeoZones) => {
        await geoZoneController
            .upsert(_filter)
            .then((_) => {
                pushSuccess('Geo-zone saved successfully');
                queryClient.invalidateQueries(['trailAuditEvent']);
                queryClient.invalidateQueries(['patrolTimeForecast']);
            })
            .catch((err) => {
                pushError(err.response.data.message);
            });
    };

    const getPhotoSnapShotSP111 = async (_filteredCamera: ISendActionGet) => {
        await sp111ConfigureRequestHttpController
            .getPhotoSnapShot(_filteredCamera)
            .then((res) => {
                setBlobBase64SP111(res);
            })
            .catch((err) => {
                pushError(err.response?.data?.message);
            });
    };

    useEffect(() => {
        const convertBase64ToImage = async () => {
            try {
                const imgElement = await base64ToHTMLImageElement(blobBase64SP111);
                setImageElementSP111(imgElement);
            } catch (error) {
                console.error('Failed to convert base64 to image', error);
            }
        };
        convertBase64ToImage();
    }, [blobBase64SP111]);

    const {
        fields: operationHours,
        update: updateOperationHours,
        append: appendOperationHours,
        remove: removeOperationHours,
    } = useFieldArray({ control, name: 'operationHours' });

    const gracePeriodForm = useFieldArray({ control, name: 'gracePeriods' });

    const handleGetListGeoZoneByView = async (_filter: IGetGeoZoneProps) => {
        setLoadingListGzByView(true);
        await geoZoneController
            .listGeoZoneByCameraView(_filter)
            .then((res) => {
                setListGeoZonesByView(res);
            })
            .catch((err) => {
                pushError(err.response?.data?.message);
            })
            .finally(() => {
                setLoadingListGzByView(false);
            });
    };

    useEffect(() => {
        handleGetListGeoZoneByView({
            pageSize: 10000,
            filter: {
                CameraId: Number(cameraId),
                CameraViewId: cameraView?.Id,
            },
        });
    }, []);

    const prevGeoZonePointsOverviewPage: GeoZoneCoordinates[][] =
        listGeoZonesByView.rows?.map((gz) => {
            const coordinatesWithGeoZone = gz.GeoZoneCoordinates?.map((coord) => ({
                ...coord,
                GeoZone: gz as GeoZones,
            }));

            return coordinatesWithGeoZone
                ?.sort((a, b) => a.Index - b.Index)
                .concat(coordinatesWithGeoZone[0]) as GeoZoneCoordinates[];
        }) ?? [];

    const prevGeoZonePointsDetailPage: GeoZoneCoordinates[][] =
        (listGeoZonesByView.rows?.map((gz) => {
            return gz.GeoZoneCoordinates?.sort((a, b) => a.Index - b.Index).concat(gz.GeoZoneCoordinates[0]);
        }) as GeoZoneCoordinates[][]) ?? [];

    const prevGeoZonePointsDetailPageCameraSP111: GeoZoneCoordinates[][] =
        (camera.GeoZones?.map((gz) => {
            return gz.GeoZoneCoordinates?.sort((a, b) => a.Index - b.Index).concat(gz.GeoZoneCoordinates[0]);
        }) as GeoZoneCoordinates[][]) ?? [];

    const contraventionAutocomplete = useStyledAutocomplete({
        list: {
            options: contraventionReasons,
            isFiltered(option, searchText) {
                return option.Summary.toLowerCase().includes(searchText.toLowerCase());
            },
        },
        dependencyList: [contraventionReasons],
    });

    const detectModeAutocomplete = useStyledAutocomplete({
        list: {
            options: detectModes,
            isFiltered(option, searchText) {
                return option.Label.toLowerCase().includes(searchText.toLowerCase());
            },
        },
    });

    const availableLocations = useMemo(() => {
        const locationMap = new Map<number, Location>();

        if (camera.Zone?.Location) {
            const primaryLoc = camera.Zone.Location;
            locationMap.set(primaryLoc.Id!, {
                ...primaryLoc,
                Zones: [camera.Zone],
            });
        }

        if (camera.ExtendedCoverage && Array.isArray(camera.ExtendedCoverage)) {
            (camera.ExtendedCoverage as Location[]).forEach((extLoc) => {
                const existingLoc = locationMap.get(extLoc.Id!);

                if (existingLoc) {
                    const mergedZones = [...(existingLoc.Zones || [])];
                    (extLoc.Zones || []).forEach((zone) => {
                        if (!mergedZones.some((z) => z.Id === zone.Id)) {
                            mergedZones.push(zone);
                        }
                    });
                    locationMap.set(extLoc.Id!, {
                        ...extLoc,
                        Zones: mergedZones,
                    });
                } else {
                    locationMap.set(extLoc.Id!, extLoc);
                }
            });
        }

        return Array.from(locationMap.values());
    }, [camera.Zone, camera.ExtendedCoverage]);

    const locationAutocomplete = useStyledAutocomplete({
        list: {
            options: availableLocations,
            isFiltered(option: Location, searchText) {
                return option.Name.toLowerCase().includes(searchText.toLowerCase());
            },
        },
        dependencyList: [availableLocations],
    });

    const zoneAutocomplete = useStyledAutocomplete({
        list: {
            options: zones,
            isFiltered(option, searchText) {
                return option.PublicName.toLowerCase().includes(searchText.toLowerCase());
            },
        },
        dependencyList: [zones],
    });

    useEffect(() => {
        setLoading(true);
        cameraController
            .get(cameraId!)
            .then(async (res) => {
                setCamera(res);
                setLoading(false);

                const filteredCamera: ISendActionGet = {
                    ip: res.IP,
                    authenticate: {
                        password: res.PassWord,
                        username: res.UserName,
                    },
                };

                setFilteredCamera(filteredCamera);

                if (isCheckGeoZoneDetail) {
                    await getPhotoSnapShotSP111(filteredCamera);
                }
            })
            .catch((err) => {
                pushError(err.response.data.message);
                navigate(`/cameras`);
            });
    }, [cameraId]);

    useEffect(() => {
        geoZoneController.getContraventionReasonTranslations().then((res) => {
            setContraventionReasons(res);
        });
    }, []);

    // Initialize Location and Zone from camera data
    useEffect(() => {
        if (camera.Zone?.Location && availableLocations.length > 0) {
            const mergedLocation = availableLocations.find((loc) => loc.Id === camera.Zone?.Location?.Id);

            if (mergedLocation) {
                setValue('location', mergedLocation);
                setZones(mergedLocation.Zones || []);

                const cameraZone = mergedLocation.Zones?.find((z) => z.Id === camera.Zone?.Id);
                setValue('zone', cameraZone || camera.Zone);
            }
        }
    }, [camera.Zone, availableLocations, setValue]);

    const handleAddGeoZoneOverview = (viewChange?: AddGzOverviewProps) =>
        handleSubmit(async (data) => {
            let view: CameraView | undefined;

            if (isTypeOverview && !isHasCameraViewZoomId) {
                const filteredData: GeoZones = {
                    CameraId: camera.Id!,
                    CameraViewId: cameraView?.Id,
                    GeoZoneType: GeoZoneType.Overview,
                    GeoZoneCoordinates: data.geoZonePoints.slice(0, data.geoZonePoints.length - 1).map((item) => ({
                        GeoZoneId: item.GeoZoneId,
                        Index: item.Index,
                        XPoint: item.XPoint,
                        YPoint: item.YPoint,
                    })),
                    GeoZoneName: data.geoZoneName.trim() || undefined,
                };

                if (viewChange) {
                    view = await handleAddView(
                        viewChange?.presetName,
                        viewChange?.photoSnapShotByPTZ,
                        viewChange?.gotoPointId
                    );
                }

                handleUpsertGeoZoneOverview({ ...filteredData, CameraViewZoomId: view?.Id }, view, viewChange?.isAddGz);
            } else if (isTypeDetails || isHasCameraViewZoomId) {
                const groupedOperationHours = groupBy(handleConvertOperationHourConfig(data.operationHours), '_fakeId');
                const filteredData: GeoZones = {
                    CameraId: camera.Id!,
                    CameraViewId: isHasCameraViewZoomId ? isHasCameraViewZoomId : cameraView?.Id,
                    GeoZoneType: GeoZoneType.Detail,
                    ContraventionReasonId: Number(data.contraventionReasonTranslations.Id!),
                    ContraventionType: data.contraventionReasonTranslations.Summary ?? '',
                    ValidationType: data.detectMode.Value,
                    Status: !!data.isActive ? Status.Active : Status.Inactive,
                    PermitExemptionsEnabled: data.isExemptVRNs,
                    GeoZoneCoordinates: data.geoZonePoints.slice(0, data.geoZonePoints.length - 1).map((item) => ({
                        GeoZoneId: item.GeoZoneId,
                        Index: item.Index,
                        XPoint: item.XPoint,
                        YPoint: item.YPoint,
                    })),
                    GZOConfigs: [
                        {
                            EffectiveFrom: moment(startDate).startOf('date').toDate(),
                            GZOConfigGracePeriods: data.gracePeriods.map(
                                (item) =>
                                    ({
                                        ...item,
                                        GracePeriod: getGracePeriods(item.GracePeriod),
                                        GZOConfigDays: groupedOperationHours[item._fakeId],
                                    }) as GeoZoneOperationConfigGracePeriodWithDay
                            ),
                            Status: false,
                        },
                    ],
                    ZoneId: data.zone?.Id,
                };

                try {
                    setIsLoadingSave(true);
                    const promises: Promise<any>[] = [];

                    if (data?.multigeoZonePoints?.length) {
                        promises.push(
                            ...data.multigeoZonePoints.map((item) =>
                                handleUpsertGeoZoneDetail({
                                    ...filteredData,
                                    GeoZoneCoordinates: item.points.slice(0, item.points.length - 1).map((p, idx) => ({
                                        GeoZoneId: p.GeoZoneId,
                                        Index: p.Index ?? idx + 1,
                                        XPoint: p.XPoint,
                                        YPoint: p.YPoint,
                                    })),
                                    TotalBay: item?.totalBay || 0,
                                    GeoZoneName: item?.geoZoneName?.trim() || undefined,
                                    ZoneId: data.zone?.Id,
                                })
                            )
                        );
                    } else {
                        promises.push(handleUpsertGeoZoneDetail(filteredData));
                    }

                    await Promise.allSettled(promises);
                    pushSuccess('Geo-zone saved successfully');
                    queryClient.invalidateQueries(['trailAuditEvent']);
                    queryClient.invalidateQueries(['patrolTimeForecast']);

                    handleNavigateButton();
                } finally {
                    setIsLoadingSave(false);
                }
            }
        });

    const handleAddView = async (
        presetName?: string,
        photoSnapShotByPTZ?: PhotoSnapShotByPTZ,
        gotoPointId?: number
    ): Promise<CameraView> => {
        setIsLoadingSave(true);
        const _filter: CameraView = {
            Id: undefined,
            CameraId: camera.Id!,
            CameraViewName: presetName || '',
            PtzPanPos: photoSnapShotByPTZ?.PTZ?.PtzPanPos!,
            PtzZoomPos: photoSnapShotByPTZ?.PTZ?.PtzZoomPos!,
            PtzTiltPos: photoSnapShotByPTZ?.PTZ?.PtzTiltPos!,
            ViewType: CameraViewType.ViewDetail,
            PresetId: gotoPointId,
        };

        return await cameraViewController
            .upsert(_filter)
            .then((res) => {
                return cameraViewController.updateLatestPhoto({
                    CameraView: res,
                    PTZCommand: {
                        authenticate: {
                            password: camera.PassWord,
                            username: camera.UserName,
                        },
                        ip: camera.IP,
                    },
                });
            })
            .finally(() => setIsLoadingSave(false));
    };

    useEffect(() => {
        if (!cameraView && camera.PTZCameraId) {
            navigate(`${pathNameBreadCrumbs}/${cameraId}`, { state: { tabValue: 2 } });
        }
    }, []);

    return {
        loading,
        errors,
        methods: methods as any,
        conditionBreadcrumbs,
        prevGeoZonePointsOverviewPage,
        prevGeoZonePointsDetailPage,
        prevGeoZonePointsDetailPageCameraSP111,
        selectedCameraType,
        gracePeriodForm,
        operationHours,
        contraventionAutocomplete,
        detectModeAutocomplete,
        locationAutocomplete,
        zoneAutocomplete,
        zones,
        setZones,
        isLoadingSave,
        imageElementSP111,
        startDate,
        state,
        isHasCameraViewZoomId,
        typeDetailsIncludesCameraViewZoomId,
        isTypeOverview,
        isTypeDetails,
        pathNameBreadCrumbs,
        setStartDate,
        updateOperationHours,
        setIsOverlap,
        navigate,
        shouldDisableButton,
        handleRadioChangeCameraType,
        handleAddGeoZoneOverview,
        handleNavigateButton,
        imageElementPTZ,
        loadingListGzByView,
        filteredCamera,
        geoZonesItem,
        listCameraView,
    };
}
